import { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

/** What kind of aside this is: how urgently it wants to be read. */
export type TraceCalloutTone = 'info' | 'warn' | 'danger' | 'ok' | 'muted';

/**
 * The word in front of each kind of aside.
 *
 * Named rather than left to the colour. A tint and a small icon are not a sentence, and the
 * three kinds of aside on this panel say genuinely different things: a caveat qualifies what
 * the step above it claims, a warning is about the environment the step sits in, and an error
 * is something that just failed. Reading the first as the third is how somebody concludes an
 * object is broken when the panel was only admitting what it cannot see.
 */
const TITLE: Record<TraceCalloutTone, string> = {
    info: 'Ter info',
    warn: 'Let op',
    danger: 'Fout',
    ok: 'Gelukt',
    muted: 'Kanttekening',
};

const ICON: Record<TraceCalloutTone, IconProp> = {
    info: 'circle-info',
    warn: 'triangle-exclamation',
    danger: 'circle-exclamation',
    ok: 'circle-check',
    muted: 'lightbulb',
};

export interface ITraceCalloutProps {
    tone: TraceCalloutTone;

    /** Overrides the standard word, or set null to leave the heading off. */
    title?: string | null;

    icon?: IconProp;

    children: ReactNode;

    /** Buttons that belong to this aside - "stacktrace ophalen", "zoek verder terug". */
    actions?: ReactNode;
}

/**
 * One aside, with a heading that says what sort of aside it is.
 *
 * Replaces the grey and amber one-liners that used to carry every qualification on this
 * panel. They were all the same size and the same weight as the rest of the text, so the
 * sentence that said "deze stap schrijft niets weg, dus dit is afgeleid" read exactly like
 * the sentence that said "van deze app is geen teken van leven" - and both read like body
 * text, which is how a reader skips them and then argues with the conclusion.
 */
export const TraceCallout = (props: ITraceCalloutProps) => {
    const heading =
        props.title === null ? null : props.title ?? TITLE[props.tone];

    return (
        <div className={`trace-callout trace-callout--${props.tone}`}>
            <FontAwesomeIcon
                icon={props.icon ?? ICON[props.tone]}
                className="trace-callout__icon"
            />

            <div className="trace-callout__body">
                {heading && (
                    <span className="trace-callout__title">{heading}</span>
                )}

                <div className="trace-callout__text">{props.children}</div>

                {props.actions && (
                    <div className="trace-callout__actions">
                        {props.actions}
                    </div>
                )}
            </div>
        </div>
    );
};
